/*
  AIProvenanceBadge — a small inline pill that flags AI-generated
  content until a practitioner has reviewed it. Used on SOAP note
  drafts, letter drafts, patient-summary panels, and handwriting
  transcriptions.

  Renders in three modes based on ``confidence`` and ``reviewed``:
    - reviewed:    green "Reviewed by <name>"
    - confidence numeric ≥ 0.8:  soft indigo "AI draft · high"
    - confidence numeric ≥ 0.5:  amber "AI draft · medium"
    - confidence numeric  < 0.5 or "low": red "AI draft · low"
    - no confidence: neutral indigo "AI draft"

  The `size` prop controls between "chip" (default; standalone) and
  "inline" (smaller, sits next to a heading).
*/
import { Sparkles, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";

function _band(c) {
  if (c === null || c === undefined) return "unknown";
  if (typeof c === "string") {
    const l = c.toLowerCase();
    if (l === "high" || l === "ok") return "high";
    if (l === "med" || l === "medium") return "medium";
    if (l === "low") return "low";
  }
  const num = Number(c);
  if (Number.isFinite(num)) {
    if (num >= 0.8) return "high";
    if (num >= 0.5) return "medium";
    return "low";
  }
  return "unknown";
}

const BAND_STYLES = {
  high:    "bg-indigo-50 border-indigo-200 text-indigo-800",
  medium:  "bg-amber-50 border-amber-200 text-amber-800",
  low:     "bg-red-50 border-red-200 text-red-800",
  unknown: "bg-indigo-50 border-indigo-200 text-indigo-800",
  reviewed:"bg-emerald-50 border-emerald-200 text-emerald-800",
};

export default function AIProvenanceBadge({
  confidence, reviewed, reviewedBy, reviewedAt,
  source, size = "chip", className, testId,
}) {
  const band = reviewed ? "reviewed" : _band(confidence);
  const isReviewed = band === "reviewed";
  const Icon = isReviewed ? ShieldCheck : Sparkles;
  const label = isReviewed
    ? `Reviewed${reviewedBy ? ` · ${reviewedBy}` : ""}`
    : band === "unknown" ? "AI draft" : `AI draft · ${band}`;
  const title = isReviewed
    ? (reviewedAt ? `Reviewed at ${new Date(reviewedAt).toLocaleString()}` : "Reviewed")
    : `${label} — this content was generated by AI. Please review before it's used clinically or sent to a client.${source ? `\n\nSource: ${source}` : ""}`;
  const isInline = size === "inline";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border font-medium",
        BAND_STYLES[band] || BAND_STYLES.unknown,
        isInline ? "text-2xs px-1.5 py-0" : "text-2xs px-2 py-0.5",
        className,
      )}
      title={title}
      data-testid={testId || "ai-provenance-badge"}
      data-ai-band={band}
    >
      <Icon className={isInline ? "h-2.5 w-2.5" : "h-3 w-3"} />
      {label}
    </span>
  );
}
